import { ChainDefaultId, ERC20Token } from "@pancakeswap/sdk";
import { gql } from "graphql-request";
import { tryParsePrice } from "hooks/v3/utils";
import { useMemo } from "react";
import useSWRImmutable from "swr/immutable";
import { statsClient, v3Clients } from "utils/graphql";

interface PoolFields {
    id: string
    version: string
    feeTier: string
    liquidity: string
    reserve0: string
    reserve1: string
    reserve2: string
    volume: string
    volume1d: string
    volume7d: string
    fee1d: string
    fee7d: string
    apr1d: string
    apr7d: string
    token0: {
      id: string
      symbol: string
      name: string
      decimals: number
    }
    token1: {
      id: string
      symbol: string
      name: string
      decimals: number
    }
    token2?: {
      id: string
      symbol: string
      name: string
      decimals: number
    }
}

export type PoolData = {
    address: `0x${string}`;
    version: string
    feeTier?: number
    liquidity: number
    reserve0: number
    reserve1: number
    reserve2?: number
    volume: number
    volume1d: number
    volume7d: number
    fee1d: number
    fee7d: number
    apr1d: number
    apr7d: number
    token0: ERC20Token
    token1: ERC20Token
    token2?: ERC20Token
}

export function useAllPools(version: string, query: string = '', sortField: string = 'liquidity', sortDesc: boolean = true, page: number = 0) {
    const { data, status } = useSWRImmutable(
        ['info/pools/all', version, query, sortField, sortDesc ? 'desc' : 'asc', page],
        async () => {
            const where = `
                where: {
                    and: [
                    ${ version ? `{ version: $version }` : '' },
                    ${
                        query 
                        ? `{or: [
                            { id: $query },
                            { token0_: { id: $query } },
                            { token0_: { name_contains_nocase: $query } },
                            { token0_: { symbol_contains_nocase: $query } },
                            { token1_: { id: $query } },
                            { token1_: { name_contains_nocase: $query } },
                            { token1_: { symbol_contains_nocase: $query } },
                            { token2_: { id: $query } },
                            { token2_: { name_contains_nocase: $query } },
                            { token2_: { symbol_contains_nocase: $query } },
                        ]}` : ''
                    }]
                }
            `
            const { pools } = await statsClient.request<{ pools: PoolFields[] }>(gql`
                query ($version: String, $query: String) {
                    pools: poolDatas(${where}, orderBy: ${sortField}, orderDirection: ${sortDesc ? 'desc' : 'asc'}, first: 20, skip: ${page * 20}) {
                        id
                        version 
                        feeTier
                        liquidity
                        reserve0
                        reserve1
                        reserve2
                        volume
                        volume1d
                        volume7d
                        fee1d
                        fee7d
                        apr1d
                        apr7d
                        token0 {
                            id
                            symbol
                            name
                            decimals
                        }
                        token1 {
                            id
                            symbol
                            name
                            decimals
                        }
                        token2 {
                            id
                            symbol
                            name
                            decimals
                        }
                    }
                }
            `, { query, version })
            return pools.map(pool => ({
                address: pool.id,
                version: pool.version,
                feeTier: Number(pool.feeTier),
                liquidity: Number(pool.liquidity),
                reserve0: Number(pool.reserve0),
                reserve1: Number(pool.reserve1),
                reserve2: pool.reserve2 ? Number(pool.reserve2) : undefined,
                volume: Number(pool.volume),
                volume1d: Number(pool.volume1d),
                volume7d: Number(pool.volume7d),
                fee1d: Number(pool.fee1d),
                fee7d: Number(pool.fee7d),
                apr1d: Number(pool.apr1d),
                apr7d: Number(pool.apr7d),
                token0: new ERC20Token(ChainDefaultId, pool.token0.id as `0x${string}`, Number(pool.token0.decimals), pool.token0.symbol, pool.token0.name),
                token1: new ERC20Token(ChainDefaultId, pool.token1.id as `0x${string}`, Number(pool.token1.decimals), pool.token1.symbol, pool.token1.name),
                token2: pool.token2 ? new ERC20Token(ChainDefaultId, pool.token2.id as `0x${string}`, Number(pool.token2.decimals), pool.token2.symbol, pool.token2.name) : undefined,
            } as PoolData))
        },
        {
            keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
    return useMemo(() => {
        return { data, status }
    }, [data, status])
}

const POOLS_QUERY_BATCH = 1000

export function usePools(addresses: string[]) {
    const { data, status } = useSWRImmutable(
        ['info/pools/addresses', addresses],
        async () => {
            const unique = [...new Set(addresses.map((addr) => addr.toLowerCase()).filter(Boolean))]
            const queries: string[][] = []
            for (let i = 0; i < unique.length; i += POOLS_QUERY_BATCH) {
                queries.push(unique.slice(i, i + POOLS_QUERY_BATCH))
            }
            const results = await Promise.all(
                queries.map((idIn) =>
                    statsClient.request<{ pools: PoolFields[] }>(gql`
                        query ($addresses: [String!]!) {
                            pools: poolDatas(where: { id_in: $addresses }, first: ${POOLS_QUERY_BATCH}) {
                                id
                                version
                                feeTier
                                liquidity
                                reserve0
                                reserve1
                                reserve2
                                volume
                                volume1d
                                volume7d
                                fee1d
                                fee7d
                                apr1d
                                apr7d
                                token0 {
                                    id
                                    symbol
                                    name
                                    decimals
                                }
                                token1 {
                                    id
                                    symbol
                                    name
                                    decimals
                                }
                                token2 {
                                    id
                                    symbol
                                    name
                                    decimals
                                }
                            }
                        }
                    `, { addresses: idIn })
                )
            )
            const pools = results.flatMap((result) => result.pools)
            return pools.map(pool => ({
                address: pool.id,
                version: pool.version,
                feeTier: Number(pool.feeTier),
                liquidity: Number(pool.liquidity),
                reserve0: Number(pool.reserve0),
                reserve1: Number(pool.reserve1),
                reserve2: pool.reserve2 ? Number(pool.reserve2) : undefined,
                volume: Number(pool.volume),
                volume1d: Number(pool.volume1d),
                volume7d: Number(pool.volume7d),
                fee1d: Number(pool.fee1d),
                fee7d: Number(pool.fee7d),
                apr1d: Number(pool.apr1d),
                apr7d: Number(pool.apr7d),
                token0: new ERC20Token(ChainDefaultId, pool.token0.id as `0x${string}`, Number(pool.token0.decimals), pool.token0.symbol, pool.token0.name),
                token1: new ERC20Token(ChainDefaultId, pool.token1.id as `0x${string}`, Number(pool.token1.decimals), pool.token1.symbol, pool.token1.name),
                token2: pool.token2 ? new ERC20Token(ChainDefaultId, pool.token2.id as `0x${string}`, Number(pool.token2.decimals), pool.token2.symbol, pool.token2.name) : undefined,
            } as PoolData))
        },
        {
            keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
    return useMemo(() => {
        return { data, status }
    }, [data, status])
}

export function usePricesIn24H(poolAddress: string) {
    const { data, status } = useSWRImmutable(
        ['info/pool/v3/prices', poolAddress],
        async () => {
            const { pools } = await v3Clients[ChainDefaultId].request<{ pools: any[] }>(gql`
                query($address: ID!) {
                    pools:poolDayDatas(where: { pool: $address }, orderBy: date, orderDirection: desc, first: 1) {
                        high, low
                    }
                }
            `, {
                address: poolAddress
            })
            if (pools?.[0])
                return [pools[0].low, pools[0].high]
            return undefined
        },
        {
            keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
    return useMemo(() => {
        return { data, status }
    }, [data, status])
}